import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentAltLines,
  faIdBadge,
  faShieldCheck,
  faUserLock,
  faUserCrown,
  faUsers,
  faAddressCard,
} from '@fortawesome/pro-duotone-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faUser, faPhoneVolume } from '@fortawesome/pro-solid-svg-icons';

// Actions
import { getAgencies } from '../../../actions/agency';

// Components
import AccessControl from './access';
import LeadsTable from './leads';
import ModalControl from './ModalControl';
import CarrierPermissions from './carrier-permissions';
import StatePermissions from './state-permissions';
import UserDetails from './details';
import ThreeWay from './three-way';
import AppointmentsProfile from './appointments';
import DisruptionAccess from './disruption-access';

function ThreeWayCallIcon() {
  return (
    <span className="fa-stack ps-1" style={{ marginRight: '0.1%' }}>
      <FontAwesomeIcon
        icon={faUser}
        className="fa-stack-1x"
        style={{
          position: 'absolute',
          top: '-20px',
          right: '-35px',
        }}
      />
      <FontAwesomeIcon
        icon={faUser}
        className="fa-stack-1x"
        style={{
          position: 'absolute',
          top: '-20px',
          left: '-35px',
        }}
      />
      <FontAwesomeIcon
        icon={faUser}
        className="fa-stack-1x"
        style={{
          position: 'absolute',
          bottom: '-35px',
        }}
      />
      <FontAwesomeIcon icon={faPhoneVolume} className="fa-stack-1x" />
    </span>
  );
}

function Profile({ modal = false, user = {} }) {
  const dispatch = useDispatch();
  const {
    agencies: { records: agencies },
    modals: { tab },
    user: {
      can_view_users: canAdmin,
      is_admin: isAdmin,
      can_edit_users: canEdit,
    },
    theme: {
      features: {
        statePermissions,
        appointmentsProfile,
        hideUserDetailsCarrierPermissions,
        hideUserDetailsAssignedLeads,
        hideUserDetailsThreeWay,
        disruptionReport,
      },
    },
  } = useSelector(store => store);

  useEffect(() => {
    if (agencies.length === 0 && isAdmin) { getAgencies(); }
  }, []);

  return (
    <>
      <div className="py-2 bg-darker d-flex justify-content-around align-items-center">
        <ModalControl title="User Details" id="details" icon={faCommentAltLines} />
        {canAdmin && (
          <>
            {canEdit && (
              <ModalControl title="Site Access" id="access" icon={faUserLock} />
            )}
            {statePermissions && (
              <ModalControl title="State Licenses" id="state" icon={faIdBadge} />
            )}
            {!hideUserDetailsCarrierPermissions && (
              <ModalControl title="Carrier Permissions" id="carrier" icon={faShieldCheck} />
            )}
            {!hideUserDetailsAssignedLeads && (
              <ModalControl title="Assigned Leads" id="leads" icon={faUsers} />
            )}
            {!hideUserDetailsThreeWay && (
              <ModalControl title="3-Way Call Recording" id="three-way" specialIcon={<ThreeWayCallIcon />} />
            )}
            {appointmentsProfile && (
              <ModalControl title="Appointments Profile" id="appointments" icon={faAddressCard} />
            )}
            {(disruptionReport && canEdit) && (
              <ModalControl title="Disruption Report Access" id="disruption" icon={faAddressCard} />
            )}
          </>
        )}

        {modal && (
          <button
            type="button"
            onClick={() => dispatch({ type: 'CLOSE_MODAL', modal: 'editInfoOpen' })}
            className="btn btn-darker ms-auto me-2 rounded-pill"
          >
            <FontAwesomeIcon fixedWidth icon={faTimes} size="2x" />
          </button>
        )}
      </div>
      <div
        className={[
          'w-100 p-4 d-flex flex-column',
          modal ? 'h-100 overflow-scroll' : '',
        ].join(' ')}
      >
        <div className="mb-2">
          <h3 className="text-capitalize d-flex mb-0">
            <strong>
              {user.first_name || user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
            </strong>
            {user.is_superuser && (
              <span className="badge bg-danger ms-3 rounded-pill">
                <FontAwesomeIcon icon={faUserCrown} className="me-2" />
                SUPERUSER
              </span>
            )}
          </h3>
          {user.npn && <p className="mb-0">{user.npn}</p>}
        </div>
        <div>
          {tab === 'details' && (
            <UserDetails user={user} />
          )}
          {tab === 'access' && (
            <AccessControl tuff="tuffVal" user={user} />
          )}
          {tab === 'state' && (
            <StatePermissions />
          )}
          {tab === 'carrier' && (
            <CarrierPermissions user={user} />
          )}
          {tab === 'leads' && (
            <LeadsTable />
          )}
          {tab === 'three-way' && (
            <ThreeWay user={user} modal={modal} />
          )}
          {tab === 'appointments' && (
            <AppointmentsProfile user={user} modal={modal} />
          )}
          {tab === 'disruption' && (
            <DisruptionAccess user={user} modal={modal} />
          )}
        </div>
      </div>
    </>
  );
}

export default Profile;
